/**
 * Property Mapper Utility
 * Transforms real portfolio property structure to CashFlow expected format
 */

/**
 * Parse suburb, state, and postcode from full address string
 * Handles Australian address formats like:
 *   "123 James Street, New Farm QLD 4005"
 *   "45 Commercial Road, Teneriffe QLD 4005"
 *   "Unit 5/78 Park Road, Sydney NSW 2000"
 *
 * @param {string} address - Full address string
 * @returns {{ suburb: string, state: string, postcode: string }}
 */
export const parseAddressComponents = (address) => {
  if (!address || typeof address !== 'string') {
    return { suburb: '', state: '', postcode: '' };
  }

  // Australian states
  const states = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
  const statePattern = states.join('|');

  // Pattern: "Street Address, Suburb STATE Postcode"
  // Captures: suburb before state, state, optional postcode
  const fullPattern = new RegExp(
    `,\\s*([A-Za-z\\s]+?)\\s+(${statePattern})\\s*(\\d{4})?\\s*$`,
    'i'
  );

  const match = address.match(fullPattern);

  if (match) {
    return {
      suburb: match[1].trim(),
      state: match[2].toUpperCase(),
      postcode: match[3] || '',
    };
  }

  // Fallback: try to extract just the state
  const stateMatch = address.match(new RegExp(`(${statePattern})`, 'i'));
  if (stateMatch) {
    // Try to get suburb from before the state
    const beforeState = address.split(stateMatch[1])[0];
    const parts = beforeState.split(',');
    const suburb = parts.length > 1 ? parts[parts.length - 1].trim() : '';

    return {
      suburb,
      state: stateMatch[1].toUpperCase(),
      postcode: '',
    };
  }

  return { suburb: '', state: '', postcode: '' };
};

/**
 * Extract street address (first part before comma)
 * @param {string} address - Full address string
 * @returns {string} Street address only
 */
export const extractStreetAddress = (address) => {
  if (!address || typeof address !== 'string') return '';
  const parts = address.split(',');
  return parts[0]?.trim() || address;
};

/**
 * Calculate loan amount from currentValue and equity
 * loanAmount = currentValue - equity
 *
 * @param {number} currentValue - Current property value
 * @param {number} equity - Current equity in property
 * @returns {number} Calculated loan amount
 */
export const calculateLoanAmount = (currentValue, equity) => {
  const value = parseFloat(currentValue) || 0;
  const eq = parseFloat(equity) || 0;
  return Math.max(0, value - eq);
};

/**
 * Calculate LVR (Loan-to-Value Ratio)
 * @param {number} loanAmount - Current loan amount
 * @param {number} propertyValue - Current property value
 * @returns {number} LVR as percentage (0-100)
 */
export const calculateLVR = (loanAmount, propertyValue) => {
  if (!propertyValue || propertyValue === 0) return 0;
  return Math.round((loanAmount / propertyValue) * 100);
};

/**
 * Transform a single property from real portfolio format to CashFlow format
 *
 * Real format (from currentPortfolio.properties):
 * {
 *   id, address, type, purchasePrice, currentValue,
 *   equity, weeklyRent, purchaseDate
 * }
 *
 * CashFlow format (expected by CashFlowTab):
 * {
 *   id, address, suburb, state, postcode,
 *   value, weeklyRent, loanAmount
 * }
 *
 * @param {Object} property - Property in real portfolio format
 * @returns {Object|null} Property in CashFlow format, or null if invalid
 */
export const mapPropertyToCashFlowFormat = (property) => {
  if (!property || !property.id) return null;

  const { suburb, state, postcode } = parseAddressComponents(property.address);
  const value = property.currentValue || property.purchasePrice || 0;
  const loanAmount = calculateLoanAmount(value, property.equity);

  return {
    id: property.id,
    address: extractStreetAddress(property.address),
    fullAddress: property.address,
    suburb,
    state,
    postcode,
    value,
    weeklyRent: property.weeklyRent || 0,
    loanAmount,
    lvr: calculateLVR(loanAmount, value),
    // Preserve original fields for reference
    _original: {
      type: property.type,
      purchasePrice: property.purchasePrice,
      purchaseDate: property.purchaseDate,
      equity: property.equity,
    },
  };
};

/**
 * Transform array of properties from real format to CashFlow format
 * @param {Array} properties - Array of properties in real format
 * @returns {Array} Array of properties in CashFlow format
 */
export const mapPropertiesToCashFlowFormat = (properties) => {
  if (!Array.isArray(properties) || properties.length === 0) {
    return [];
  }
  return properties.map(mapPropertyToCashFlowFormat).filter(Boolean);
};

/**
 * Get properties from investorProfile with format transformation
 * Checks both existingProperties (legacy) and currentPortfolio.properties (real)
 *
 * @param {Object} investorProfile - Investor profile object
 * @returns {Array} Properties in CashFlow-compatible format
 */
export const getPropertiesForCashFlow = (investorProfile) => {
  if (!investorProfile) return [];

  // Check real data location first (currentPortfolio.properties)
  if (investorProfile.currentPortfolio?.properties?.length > 0) {
    return mapPropertiesToCashFlowFormat(investorProfile.currentPortfolio.properties);
  }

  // Fallback to legacy format (existingProperties - already in correct structure)
  if (investorProfile.existingProperties?.length > 0) {
    // Legacy format might already be correct, but ensure consistency
    return investorProfile.existingProperties.map((prop) => ({
      id: prop.id,
      address: extractStreetAddress(prop.address),
      fullAddress: prop.address,
      suburb: prop.suburb || '',
      state: prop.state || '',
      postcode: prop.postcode || '',
      value: prop.value || prop.currentValue || 0,
      weeklyRent: prop.weeklyRent || 0,
      loanAmount: prop.loanAmount || 0,
      lvr: calculateLVR(prop.loanAmount, prop.value || prop.currentValue),
      _original: prop,
    }));
  }

  return [];
};

/**
 * Calculate aggregate portfolio totals from properties
 * @param {Array} properties - Array of properties (in CashFlow format)
 * @returns {Object} Aggregated portfolio totals
 */
export const calculatePortfolioTotals = (properties) => {
  if (!Array.isArray(properties) || properties.length === 0) {
    return {
      totalValue: 0,
      totalWeeklyRent: 0,
      totalLoanAmount: 0,
      totalEquity: 0,
      averageLVR: 0,
      propertyCount: 0,
    };
  }

  const totals = properties.reduce(
    (acc, prop) => ({
      totalValue: acc.totalValue + (prop.value || 0),
      totalWeeklyRent: acc.totalWeeklyRent + (prop.weeklyRent || 0),
      totalLoanAmount: acc.totalLoanAmount + (prop.loanAmount || 0),
    }),
    { totalValue: 0, totalWeeklyRent: 0, totalLoanAmount: 0 }
  );

  const totalEquity = totals.totalValue - totals.totalLoanAmount;
  const averageLVR = totals.totalValue > 0
    ? Math.round((totals.totalLoanAmount / totals.totalValue) * 100)
    : 0;

  return {
    ...totals,
    totalEquity,
    averageLVR,
    propertyCount: properties.length,
  };
};

// Phase 1+ (single-source-of-truth): the legacy demo property fixtures
// were removed — same bug class as the Sarah Chen mock. Fake property
// data that could leak into a real user's portfolio if accidentally
// imported. Use canonical PortfolioDataContext.properties for all reads.
// CI guard: noMockLeaks.test.js prevents re-introduction.

/**
 * Initialize cost base for a property that doesn't have one
 * Also normalizes field names to ensure loanAmount is always present
 * @param {Object} property - Property object
 * @returns {Object} Property with initialized cost base and normalized fields
 */
export const initializePropertyCostBase = (property) => {
  // Get current value (handle multiple field name formats)
  const currentValue = property.currentValue ?? property.current_value ?? property.value ?? 0;

  // Calculate/normalize loan amount
  // Priority: explicit loanAmount > loan_amount > loan > calculated from equity
  let loanAmount = property.loanAmount ?? property.loan_amount ?? property.loan;
  if (loanAmount === undefined || loanAmount === null) {
    // Calculate from equity if available
    const equity = property.equity ?? property.totalEquity ?? 0;
    loanAmount = equity > 0 ? Math.max(0, currentValue - equity) : 0;
  }

  // Normalize weekly rent
  const weeklyRent = property.weeklyRent ?? property.weekly_rent ?? property.rent ?? 0;

  // If already has costBase and normalized fields, return early
  if (property.costBase && property.loanAmount !== undefined) {
    return property;
  }

  const purchasePrice = property._original?.purchasePrice || property.purchasePrice || currentValue || 0;
  const state = property.state || 'NSW';

  // Simplified stamp duty estimate (4% average)
  const stampDutyRate = state === 'VIC' ? 0.055 : state === 'QLD' ? 0.0475 : 0.045;
  const stampDuty = Math.round(purchasePrice * stampDutyRate);

  return {
    ...property,
    // Normalized field names (camelCase)
    currentValue,
    loanAmount,
    weeklyRent,
    purchaseDate: property.purchaseDate || property.purchase_date || property._original?.purchaseDate,
    costBase: property.costBase || {
      purchasePrice,
      stampDuty,
      legalFees: 1500,
      buildingInspection: 700,
      improvements: [],
      totalCostBase: purchasePrice + stampDuty + 1500 + 700,
    },
  };
};
